// src/OmniContext.jsx
// Wibe3 (Omni / Intents) connector + helpers

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ICON_URL, PROJECT_NAME, PUBLIC_APP_URL } from "./projectMeta";
import { useWalletConnect } from "./WalletConnectContext";
import { isConfiguredAdmin } from "./utils/adminAccess";

const walletConnectProjectId =
  import.meta.env.VITE_WIBE3_PROJECT_ID ||
  import.meta.env.VITE_WC_PROJECT_ID ||
  "";

// WalletConnect is intentionally disabled by Vite compatibility aliases. Wibe3
// 1.0.27 still initializes unused chain connectors and requires a non-empty
// projectId on those disabled paths, so this sentinel is not a credential.
const wibe3ProjectId = walletConnectProjectId || "nuvelor-walletconnect-disabled";

export const HOT_CONNECTOR_STATUS = Object.freeze({
  DISABLED: "disabled",
  INITIALIZING: "initializing",
  AUTHENTICATING: "authenticating",
  READY: "ready",
  ERROR: "error",
});

export const loadWibe3Runtime = () => import("@hot-labs/wibe3");

const tonWalletsUrl =
  import.meta.env.VITE_WIBE3_TON_MANIFEST ||
  "http://localhost:1241/hot-connector/tonconnect-manifest.json";

const appUrl =
  import.meta.env.DEV && typeof window !== "undefined"
    ? window.location.origin
    : PUBLIC_APP_URL;

const createMemoryStorage = () => {
  const store = new Map();
  return {
    getItem: (key) => (store.has(key) ? store.get(key) : null),
    setItem: (key, value) => store.set(key, String(value)),
    removeItem: (key) => store.delete(key),
    clear: () => store.clear(),
  };
};

const safeStorage = (() => {
  if (typeof window === "undefined") return createMemoryStorage();
  try {
    return window.localStorage;
  } catch {
    return createMemoryStorage();
  }
})();

const OMNI_MODE_KEY = "nvl:omniEnabled:v1";

const readOmniEnabled = () => {
  try {
    return safeStorage.getItem(OMNI_MODE_KEY) === "1";
  } catch {
    return false;
  }
};

function errorFrom(cause, fallback) {
  if (cause instanceof Error) return cause;
  return new Error(String(cause?.message || cause || fallback || "HOT connector initialization failed"));
}

export function addCanonicalTransferIntent(intents, { assetId, recipient, amountAtomic }) {
  const canonicalAssetId = String(assetId || "").trim();
  const receiverId = String(recipient || "").trim().toLowerCase();
  const amount = BigInt(amountAtomic ?? 0);

  if (!canonicalAssetId.startsWith("nep141:") && !canonicalAssetId.startsWith("nep245:")) {
    throw new Error("Unsupported HOT asset");
  }
  if (!receiverId || amount <= 0n) throw new Error("Invalid HOT transfer");
  if (!Array.isArray(intents?.intents)) throw new Error("HOT intents builder is incompatible");

  // Wibe3 1.0.27 exposes addRawIntent(), but its transfer branch delegates to
  // batchTransfer() and still resolves the token registry. The public intents
  // array is therefore the only 1.0.27-compatible path for an already-canonical
  // arbitrary NEP-141/NEP-245 asset id. Withdraw uses the same public builder
  // surface below; execute(checkTokens:false) avoids registry token checks.
  intents.intents.push({
    intent: "transfer",
    receiver_id: receiverId,
    tokens: { [canonicalAssetId]: amount.toString() },
  });
  return intents;
}

export function repairWibe3DepositOriginalTokenAlias(instance, tokenAddress) {
  const address = String(tokenAddress || "").trim();
  const tokenList = Array.isArray(instance?.tokens) ? instance.tokens : [];
  if (!address || tokenList.length === 0) return false;

  const omniToken = tokenList.find((token) =>
    String(token?.address || "") === address &&
    token?.originalChain != null &&
    token?.chain !== token?.originalChain
  );
  if (!omniToken) return false;

  const expectedOriginalAddress = String(omniToken.originalAddress || "");
  const directOriginal = tokenList.find((token) =>
    token !== omniToken &&
    token?.chain === omniToken.originalChain &&
    String(token?.address || "") === expectedOriginalAddress
  );
  if (directOriginal) return false;

  const aliasOriginal = tokenList.find((token) =>
    token !== omniToken &&
    token?.chain === omniToken.originalChain &&
    String(token?.omniAddress || "") === address &&
    !!token?.address
  );
  if (!aliasOriginal) return false;

  // Wibe3 1.0.27 normalizes the NEAR-side wrap.near token to address
  // "native", while its deposit() lookup still compares against the OMNI
  // token's originalAddress ("wrap.near"). Repair that vendor alias in the
  // shared token object before calling deposit(); this is a no-op once the
  // upstream registry exposes a directly matching original token.
  omniToken.originalAddress = aliasOriginal.address;
  return true;
}

function findAuthenticatedNearWallet(instance, accountId) {
  const expected = String(accountId || "").trim().toLowerCase();
  if (!expected) return null;
  return (instance?.wallets || []).find((wallet) =>
    String(wallet?.address || "").trim().toLowerCase() === expected
  ) || null;
}

async function waitForAuthenticatedWallet(instance, accountId, timeoutMs = 8000) {
  const existing = findAuthenticatedNearWallet(instance, accountId);
  if (existing) return existing;
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe?.();
      callback(value);
    };
    const timer = setTimeout(() => finish(
      reject,
      new Error(`HOT connector did not authenticate ${accountId} within ${timeoutMs}ms`)
    ), timeoutMs);
    unsubscribe = instance?.onConnect?.(({ wallet } = {}) => {
      const expected = String(accountId || "").trim().toLowerCase();
      const actual = String(wallet?.address || "").trim().toLowerCase();
      if (expected && actual === expected) finish(resolve, wallet);
    }) || (() => {});
    const afterSubscribe = findAuthenticatedNearWallet(instance, accountId);
    if (afterSubscribe) finish(resolve, afterSubscribe);
  });
}

const OmniContext = createContext({
  isConfigured: false,
  connectorStatus: HOT_CONNECTOR_STATUS.DISABLED,
  connectorReady: false,
  connectorError: null,
  isConnecting: false,
  error: null,
  wallets: [],
  tokens: [],
  omniTokens: [],
  omniBalances: {},
  omniEnabled: false,
  isHotAdmin: false,
  setOmniEnabled: () => {},
  connect: async () => {},
  refresh: async () => {},
  deposit: async () => {},
  withdraw: async () => {},
  withdrawIntent: async () => {},
  transferIntent: async () => {},
});

function snapshot(instance) {
  try {
    return {
      wallets: [...(instance?.wallets || [])],
      tokens: [...(instance?.tokens || [])],
    };
  } catch (e) {
    console.warn("[omni] snapshot error", e);
    return { wallets: [], tokens: [] };
  }
}

export function OmniProvider({ children, wibe3Loader = loadWibe3Runtime }) {
  const { accountId, selector } = useWalletConnect() || {};
  const normalizedAccountId = String(accountId || "").trim().toLowerCase();
  const isHotAdmin = useMemo(() => isConfiguredAdmin(accountId), [accountId]);
  const [wallets, setWallets] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [connectorStatus, setConnectorStatus] = useState(HOT_CONNECTOR_STATUS.DISABLED);
  const [balanceRevision, setBalanceRevision] = useState(0);
  const [wibe3Instance, setWibe3Instance] = useState(null);
  const [wibe3Api, setWibe3Api] = useState(null);
  const [omniRequested, setOmniEnabledState] = useState(readOmniEnabled);
  const omniEnabled = omniRequested;
  const hotSessionKey = omniEnabled ? normalizedAccountId : "";
  const hotSessionRef = useRef(hotSessionKey);
  const instanceRef = useRef(wibe3Instance);
  hotSessionRef.current = hotSessionKey;
  instanceRef.current = wibe3Instance;

  const setOmniEnabled = useCallback((value) => {
    const next = Boolean(value);
    setOmniEnabledState(next);
    try {
      safeStorage.setItem(OMNI_MODE_KEY, next ? "1" : "0");
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    setIsConnecting(false);
    setWallets([]);
    setTokens([]);
    setError(null);
    setWibe3Instance(null);
    setWibe3Api(null);
    setConnectorStatus(HOT_CONNECTOR_STATUS.DISABLED);
    if (!omniEnabled || !hotSessionKey || !selector) return undefined;

    const sessionKey = hotSessionKey;
    let cancelled = false;
    let cleanupReplacement = () => {};
    setConnectorStatus(HOT_CONNECTOR_STATUS.INITIALIZING);

    (async () => {
      try {
        const mod = await wibe3Loader();
        if (cancelled || hotSessionRef.current !== sessionKey) return;
        if (mod?.__isStub) throw new Error("HOT Wibe3 runtime is disabled");
        const { HotConnector, WalletType, Intents } = mod || {};
        if (typeof HotConnector !== "function" || !WalletType || typeof Intents !== "function") {
          throw new Error("HOT Wibe3 runtime exports are incomplete");
        }

        const instance = new HotConnector({
          projectId: wibe3ProjectId,
          tonWalletsUrl,
          storage: safeStorage,
          connectors: [WalletType.NEAR],
          metadata: {
            name: PROJECT_NAME,
            description: `${PROJECT_NAME} Web3`,
            url: appUrl,
            icons: [ICON_URL],
          },
        });

        // Wibe3 1.0.27 ignores options.connectors and eagerly builds every chain.
        // Replace them with NEAR wired to NUVELOR's already-authenticated selector.
        if (typeof mod.near === "function") {
          for (const connector of instance.connectors || []) connector?.removeAllListeners?.();
          const nearConnector = mod.near({ connector: selector })(instance);
          const offConnect = nearConnector.onConnect?.((payload) => instance.events?.emit?.("connect", payload));
          const offDisconnect = nearConnector.onDisconnect?.((payload) => instance.events?.emit?.("disconnect", payload));
          instance.connectors = [nearConnector];
          cleanupReplacement = () => {
            offConnect?.();
            offDisconnect?.();
            nearConnector.removeAllListeners?.();
          };
        }

        if (cancelled || hotSessionRef.current !== sessionKey) return;
        setWibe3Api({ WalletType, Intents });
        setWibe3Instance(instance);
        setConnectorStatus(HOT_CONNECTOR_STATUS.AUTHENTICATING);
        await waitForAuthenticatedWallet(instance, sessionKey);
        if (cancelled || hotSessionRef.current !== sessionKey) return;

        const state = snapshot(instance);
        setWallets(state.wallets);
        setTokens(state.tokens);
        setError(null);
        setConnectorStatus(HOT_CONNECTOR_STATUS.READY);
      } catch (cause) {
        if (!cancelled && hotSessionRef.current === sessionKey) {
          const nextError = errorFrom(cause, "HOT connector initialization failed");
          console.warn("[omni] init failed", nextError);
          setWibe3Instance(null);
          setWibe3Api(null);
          setError(nextError);
          setConnectorStatus(HOT_CONNECTOR_STATUS.ERROR);
        }
      }
    })();

    return () => {
      cancelled = true;
      cleanupReplacement();
    };
  }, [hotSessionKey, omniEnabled, selector, wibe3Loader]);

  const refresh = useCallback(async () => {
    if (!omniEnabled || !hotSessionKey || !wibe3Instance) return;
    const sessionKey = hotSessionKey;
    const instance = wibe3Instance;
    try {
      for (const wallet of instance.wallets || []) {
        await instance.fetchTokens(wallet).catch((e) => {
          console.warn("[omni] fetchTokens failed", e);
        });
        await instance.fetchOmniTokens(wallet).catch((e) => {
          console.warn("[omni] fetchOmniTokens failed", e);
        });
      }
      if (hotSessionRef.current !== sessionKey || instanceRef.current !== instance) return;
      const state = snapshot(instance);
      setWallets(state.wallets);
      setTokens(state.tokens);
    } catch (e) {
      if (hotSessionRef.current !== sessionKey || instanceRef.current !== instance) return;
      console.warn("[omni] refresh error", e);
      setError(e);
    }
  }, [hotSessionKey, omniEnabled, wibe3Instance]);

  const refreshAfterMutation = useCallback(() => {
    // Transaction completion must not be blocked by secondary balance/token reads.
    // Close/reset UI immediately after the signed operation succeeds, then refresh
    // HOT/Intents state in the background. balanceRevision lets consumers refresh
    // their direct intents.near reads without coupling the mutation promise to RPC.
    setBalanceRevision((value) => value + 1);
    Promise.resolve().then(() => refresh()).catch((cause) => {
      console.warn("[omni] post-mutation refresh failed", cause);
    });
  }, [refresh]);

  useEffect(() => {
    if (!hotSessionKey || !wibe3Instance) return undefined;
    const sessionKey = hotSessionKey;
    const instance = wibe3Instance;
    const isCurrentSession = () =>
      hotSessionRef.current === sessionKey && instanceRef.current === instance;
    const unsubConnect = instance.onConnect?.(() => {
      if (isCurrentSession()) refresh();
    });
    const unsubDisconnect = instance.onDisconnect?.(() => {
      if (!isCurrentSession()) return;
      const state = snapshot(instance);
      setWallets(state.wallets);
      setTokens(state.tokens);
    });

    return () => {
      unsubConnect?.();
      unsubDisconnect?.();
    };
  }, [hotSessionKey, refresh, wibe3Instance]);

  const connectorReady = connectorStatus === HOT_CONNECTOR_STATUS.READY && !!wibe3Instance;

  const requireReadyConnector = useCallback(() => {
    if (!omniEnabled) throw new Error("HOT Network is disabled");
    if (connectorReady && wibe3Instance) return wibe3Instance;
    if (error) throw error;
    if (connectorStatus === HOT_CONNECTOR_STATUS.AUTHENTICATING) {
      throw new Error("HOT connector is authenticating");
    }
    if (connectorStatus === HOT_CONNECTOR_STATUS.INITIALIZING) {
      throw new Error("HOT connector is initializing");
    }
    throw new Error("HOT connector is unavailable");
  }, [connectorReady, connectorStatus, error, omniEnabled, wibe3Instance]);

  const connect = useCallback(
    async (type) => {
      let instance;
      try {
        instance = requireReadyConnector();
      } catch (cause) {
        const nextError = errorFrom(cause);
        setError(nextError);
        throw nextError;
      }
      setIsConnecting(true);
      try {
        await instance.connect(type || wibe3Api?.WalletType?.NEAR);
        await refresh();
        setError(null);
      } catch (cause) {
        const nextError = errorFrom(cause, "HOT connect failed");
        console.warn("[omni] connect failed", nextError);
        setError(nextError);
        throw nextError;
      } finally {
        setIsConnecting(false);
      }
    },
    [refresh, requireReadyConnector, wibe3Api]
  );

  const omniTokens = useMemo(() => {
    if (!wibe3Api?.WalletType) return [];
    return tokens.filter((t) => t.type === wibe3Api.WalletType.OMNI);
  }, [tokens, wibe3Api]);

  const omniBalances = useMemo(() => {
    if (!wibe3Instance) return {};
    const map = {};
    omniTokens.forEach((token) => {
      const summary = wibe3Instance.omniBalance(token.address);
      map[token.address] = {
        token,
        omni: summary.omni,
        onchain: summary.onchain,
        total: summary.total,
      };
    });
    return map;
  }, [omniTokens, wallets, wibe3Instance]);

  const deposit = useCallback(
    async (tokenAddress, amount) => {
      const instance = requireReadyConnector();
      repairWibe3DepositOriginalTokenAlias(instance, tokenAddress);
      const result = await instance.deposit(tokenAddress, amount, {
        title: "Deposit to HOT",
      });
      refreshAfterMutation();
      return result;
    },
    [refreshAfterMutation, requireReadyConnector]
  );

  const withdraw = useCallback(
    async (tokenAddress, amount) => {
      const instance = requireReadyConnector();
      const result = await instance.withdraw(tokenAddress, amount, {
        title: "Withdraw from HOT",
        sender: instance.priorityWallet,
      });
      refreshAfterMutation();
      return result;
    },
    [refreshAfterMutation, requireReadyConnector]
  );

  const withdrawIntent = useCallback(
    async ({ assetId, amountAtomic, receiver }) => {
      if (!assetId || !receiver) throw new Error("Invalid withdraw params");
      const instance = requireReadyConnector();
      if (!wibe3Api?.Intents) throw new Error("HOT intents not ready");
      const wallet = instance.wallets.find((w) => !!w.omniAddress);
      if (!wallet) throw new Error("HOT wallet not connected");

      const intents = new wibe3Api.Intents(instance).attachWallet(wallet);
      if (assetId.startsWith("nep141:")) {
        intents.intents.push({
          intent: "ft_withdraw",
          receiver_id: receiver,
          token: assetId.slice("nep141:".length),
          amount: String(amountAtomic),
        });
      } else if (assetId.startsWith("nep245:")) {
        const parts = assetId.split(":");
        const mtContract = parts[1];
        const tokenId = parts.slice(2).join(":");
        intents.intents.push({
          intent: "mt_withdraw",
          receiver_id: receiver,
          token: mtContract,
          token_ids: [tokenId],
          amounts: [String(amountAtomic)],
        });
      } else {
        throw new Error("Unsupported assetId");
      }

      const hash = await intents.execute({ checkTokens: false });
      refreshAfterMutation();
      return hash;
    },
    [refreshAfterMutation, requireReadyConnector, wibe3Api]
  );

  const transferIntent = useCallback(
    async ({ assetId, transfers }) => {
      if (!assetId || !Array.isArray(transfers) || transfers.length === 0) {
        throw new Error("Invalid HOT transfer params");
      }
      const instance = requireReadyConnector();
      if (!wibe3Api?.Intents) throw new Error("HOT intents not ready");
      const wallet = instance.wallets.find((w) => !!w.omniAddress);
      if (!wallet) throw new Error("HOT wallet not connected");

      const intents = new wibe3Api.Intents(instance).attachWallet(wallet);
      for (const transfer of transfers) {
        if (!transfer?.recipient || transfer?.amountAtomic == null) {
          throw new Error("Invalid HOT transfer");
        }
        addCanonicalTransferIntent(intents, {
          assetId,
          recipient: transfer.recipient,
          amountAtomic: transfer.amountAtomic,
        });
      }
      const hash = await intents.execute({ checkTokens: false });
      refreshAfterMutation();
      return hash;
    },
    [refreshAfterMutation, requireReadyConnector, wibe3Api]
  );



  return (
    <OmniContext.Provider
      value={{
        isConfigured: connectorReady,
        connectorStatus,
        connectorReady,
        connectorError: error,
        balanceRevision,
        walletConnectConfigured: !!walletConnectProjectId,
        isConnecting,
        error,
        wallets,
        tokens,
        omniTokens,
        omniBalances,
        omniEnabled,
        isHotAdmin,
        setOmniEnabled,
        connect,
        refresh,
        deposit,
        withdraw,
        withdrawIntent,
        transferIntent,
      }}
    >
      {children}
    </OmniContext.Provider>
  );
}

export function useOmni() {
  return useContext(OmniContext);
}
